import React from "react";
import { Droplets, Flame, Biohazard, Hammer, ShieldAlert, Wrench, Clock, PhoneCall, ArrowRight, FileCheck2, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
export default function HowardCountyEmergencyServices() {
  const serviceCards = [{
    title: "Water Damage Restoration",
    link: "/WaterDamageFloodRestorationPage",
    icon: Droplets,
    content: "Slab leaks, ruptured supply lines, and seasonal storms across Columbia's multi-level townhomes and Route 29 commercial buildings require immediate structural extraction. TriServe mobilizes high-volume water extractors, desiccant dehumidifiers, and thermal hygrometers to track hidden moisture traveling through subfloors, drywall assemblies, and finished basement living areas."
  }, {
    title: "24/7 Emergency Flood Cleanup",
    link: "/WaterDamageFloodRestorationPage",
    icon: Droplets,
    content: "Severe weather funneled into the steep river valley of Historic Ellicott City and low-elevation zones along the Patapsco and Little Patuxent rivers can inundate properties in minutes. Our rapid-response crews deploy heavy submersible pumps, industrial trash pumps, and antimicrobial washdowns to remove sediment, silt, and standing water before structural decay begins."
  }, {
    title: "Sewage Backup Cleanup",
    link: "/SewageCleanupPage",
    icon: Biohazard,
    content: "Municipal main surcharges, root intrusions, and failed ejector pumps create hazardous Category 3 blackwater backups in basements and ground-floor bathrooms throughout Howard County. We seal off contaminated zones, safely extract biohazardous waste, discard non-salvageable porous padding, and sanitize every affected structural surface with hospital-grade antimicrobial solutions."
  }, {
    title: "Fire & Smoke Damage Restoration",
    link: "/FireSmokeOdorRestorationPage",
    icon: Flame,
    content: "Kitchen blazes, electrical malfunctions, and furnace puff-backs leave corrosive soot, acidic residue, and toxic smoke odors across walls, cabinetry, and HVAC ductwork. TriServe provides emergency weather board-up, structural shoring, comprehensive soot soot mitigation, and HEPA air scrubbing paired with thermal fogging to eliminate airborne smoke particulates."
  }, {
    title: "Mold Remediation",
    link: "/MoldRemediationPage",
    icon: ShieldAlert,
    content: "Mid-Atlantic summer humidity combined with chronic crawlspace condensation or slow foundation seepage often triggers hidden fungal colonization in older and newer Howard County homes. Our field teams erect critical negative-air containment barriers, deploy commercial HEPA filtration, safely remove compromised materials, and apply antifungal treatments to restore healthy indoor air."
  }, {
    title: "Structural Reconstruction",
    link: "/ReconstructionDesignBuildPage",
    icon: Hammer,
    content: "When severe water intrusion, fire compromise, or storm collapse requires more than drying, our skilled tradesmen handle seamless rebuilding from the framing out. We restore load-bearing studs, replace drywall and insulation, rebuild subfloors, and match trim and finishes so residential and commercial properties return to sound pre-loss condition."
  }, {
    title: "General Emergency Property Restoration",
    link: null,
    icon: Wrench,
    content: "From sudden roof breaches along the I-95 business corridor to frozen burst pipes during deep winter freezes, TriServe provides total emergency stabilization. We arrive equipped to arrest active water sources, install temporary protective barriers, and secure properties 24 hours a day, 365 days a year across every corner of Howard County."
  }];
  return <section id="howardcountyemergencyservices" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-3 text-primary border-primary/30">
            Howard County Coverage
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Emergency property restoration and disaster mitigation in Howard County.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            When burst supply pipes, flash floods along the Patapsco basin, or electrical fires threaten your property, TriServe delivers immediate on-site stabilization. Operating as a mobile response force with no walk-in office in Maryland, our local technicians dispatch directly to your door, bypassing call centers to protect your home or facility from secondary structural damage.
          </p>
        </div>

        {/* Dispatch & Operations Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 p-6 sm:p-8 rounded-xl bg-muted/50 border border-border">
          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">
                ~60-Minute Local Deployment
              </h3>
              <p className="text-sm text-muted-foreground mt-1 leading-normal">
                Direct mobile dispatch units stationed along Route 29, Route 40, and the I-95 corridor arrive quickly with extraction pumps and drying equipment.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
              <PhoneCall className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">
                Local Crews, No Call Centers
              </h3>
              <p className="text-sm text-muted-foreground mt-1 leading-normal">
                Calling 855-435-6631 connects you immediately to dedicated restoration crews who understand Howard County topography and start dispatching trucks.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
              <FileCheck2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">
                Direct Insurance Coordination
              </h3>
              <p className="text-sm text-muted-foreground mt-1 leading-normal">
                We document moisture mapping, photograph on-site damage, and submit line-item estimates directly to your adjuster to expedite claims handling.
              </p>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-16">
          {serviceCards.map((service, index) => {
          const IconComponent = service.icon;
          const isFullSpan = service.title === "General Emergency Property Restoration" && service.link === null;
          if (isFullSpan) {
            return <Card key={service.title} data-index={index} className="md:col-span-2 lg:col-span-3 border-border bg-card shadow-xs hover:shadow-md transition-shadow p-6">
                  <div className="flex flex-col md:flex-row gap-6 items-center">
                    <div className="w-full md:w-1/2">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-3">
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <CardTitle asChild className="text-xl font-bold text-card-foreground">
                        <h3>{service.title}</h3>
                      </CardTitle>
                      <p className="text-sm text-muted-foreground leading-relaxed mt-3 mb-4">
                        {service.content}
                      </p>
                      <div className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        <span>Available 24/7 in Howard County</span>
                      </div>
                    </div>

                    <div className="w-full md:w-1/2 h-64 sm:h-72 md:h-60 lg:h-64">
                      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/howard_county_emergency_pipe_burst_extraction_restoration.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/howard_county_emergency_pipe_burst_extraction_restoration.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/howard_county_emergency_pipe_burst_extraction_restoration.png-wvc-sizes" alt="TriServe restoration technicians performing emergency water extraction and pipe rupture cleanup with professional drying equipment in a Howard County, MD property" width={600} height={400} loading="lazy" decoding="async" className="w-full h-full object-cover rounded-lg border border-border" />
                    </div>
                  </div>
                </Card>;
          }
          return <Card key={service.title} data-index={index} className="flex flex-col justify-between border-border bg-card shadow-xs hover:shadow-md transition-shadow">
                <div>
                  <CardHeader className="pb-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-3">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <CardTitle asChild className="text-xl font-bold text-card-foreground">
                      <h3>{service.title}</h3>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 pb-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.content}
                    </p>
                  </CardContent>
                </div>

                <div className="px-6 pb-6 pt-2">
                  {service.link ? <Button variant="link" className="p-0 h-auto font-medium text-primary hover:text-primary/80 inline-flex items-center gap-1.5" asChild>
                      <Link to={service.link}>
                        <span>Explore {service.title}</span>
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </Button> : <div className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      <span>Available 24/7 in Howard County</span>
                    </div>}
                </div>
              </Card>;
        })}
        </div>

        {/* Action Footer */}
        <div className="p-6 sm:p-8 rounded-xl bg-card border border-border flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xs">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Experiencing active water or property damage in Howard County?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Speak directly with an on-duty technician at 855-435-6631 for immediate ~60-minute emergency dispatch.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild size="lg">
              <Link to="/ContactRequestServicePage">Contact our team</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/OurProcessPage">View our process</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}